import { Controller } from "@hotwired/stimulus";

/**
 * Swipe-left action tray on a row: dragging the content layer left reveals the
 * buttons behind it. The row carries `touch-pan-y`, so vertical scrolling stays
 * native (the browser fires pointercancel) while horizontal drags reach us.
 * Touch pointers get implicit pointer capture, so no explicit capture is needed.
 *
 * Whether a row swipes at all is the tray's CSS to decide — callers hide it on
 * whatever they split on (the inbox on width, roll call on pointer capability)
 * and this stands down to match, so the two can never disagree.
 */
export default class InboxSwipeController extends Controller<HTMLElement> {
  static targets = ["content", "tray"];

  declare readonly contentTarget: HTMLElement;
  declare readonly trayTarget: HTMLElement;

  private startX = 0;
  private delta = 0;
  private offset = 0; // committed position: 0 (closed) or -trayWidth (open)
  private dragging = false;
  private swiped = false; // suppress the click that follows a drag

  start(event: PointerEvent) {
    if (this.trayHidden()) return;
    this.dragging = true;
    this.swiped = false;
    this.startX = event.clientX;
    this.delta = 0;
    this.contentTarget.style.transition = "none";
  }

  move(event: PointerEvent) {
    if (!this.dragging) return;
    this.delta = event.clientX - this.startX;
    const position = Math.min(0, Math.max(-this.trayWidth(), this.offset + this.delta));
    this.contentTarget.style.transform = `translateX(${position}px)`;
  }

  end() {
    if (!this.dragging) return;
    this.dragging = false;
    this.contentTarget.style.transition = "";
    if (Math.abs(this.delta) > 10) this.swiped = true;
    this.setOpen(this.offset + this.delta < -this.trayWidth() / 2);
  }

  // The browser claimed the gesture (vertical scroll) — snap back to the
  // committed position.
  cancel() {
    if (!this.dragging) return;
    this.dragging = false;
    this.contentTarget.style.transition = "";
    this.setOpen(this.offset !== 0);
  }

  /**
   * Capture-phase click filter: swallow the click generated by a drag, and
   * while the tray is open let tray taps act but turn any other tap into
   * "close" instead of opening the entry.
   */
  interceptClick(event: Event) {
    if (this.swiped) {
      this.swiped = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.offset === 0) return;

    // A tray tap acts (mark read/unread, delete) — let it through, but slide
    // the row shut so it isn't left mid-swipe after the action lands.
    if (this.trayTarget.contains(event.target as Node)) {
      this.setOpen(false);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.setOpen(false);
  }

  // Only one tray open at a time: rows listen for each other's open events.
  closeOnOther(event: Event) {
    if (event.target === this.element) return;
    if (this.offset !== 0) this.setOpen(false);
  }

  private setOpen(open: boolean) {
    this.offset = open ? -this.trayWidth() : 0;
    this.contentTarget.style.transform = open ? `translateX(${this.offset}px)` : "";
    if (open) this.dispatch("open");
  }

  private trayHidden(): boolean {
    return getComputedStyle(this.trayTarget).display === "none";
  }

  // offsetWidth is 0 in jsdom — fall back to the tray's rendered width.
  private trayWidth(): number {
    return this.trayTarget.offsetWidth || 128;
  }
}
